import { api } from '../../../../lib/api';
import type { LeadHistoryPage } from '../../../../lib/speedToLead/leadHistory';
import { funnelQueryString, leadQueryString } from '../../../../lib/leads/leadQuery';
import { splitQueryString } from '../../../../lib/leads/leadSplit';
import type { LeadCallOptions, LeadSplitDetail, LeadSplitFilters, LeadSplitPage } from '../../../../types/leadSplit';
import type { LeadFilters, LeadFunnel, LeadListPage } from '../../../../types/leadList';
import type { LeadOutcome } from '../../../../types/leadOutcome.js';
import type { CallLogRow } from '../../../../types/leadCall';
import type { SpeedToLeadStaffEntry, StaffRow } from '../../../../types/speedToLead';

// CLIENT PORTAL "who we call": the club's own staff call list. ⚠️ Everything here hits
// /api/portal/speed-to-lead behind requirePortalAuth. The server resolves the scope from the session; the
// club id only ever selects WITHIN it (an id outside it is a 404), and the only write is the staff list.
// This file never imports the Kyck-side api (a source guard pins it).

const BASE = '/api/portal/speed-to-lead';

export interface PortalSpeedToLeadClub {
  accountId: string;
  name: string;
  rows: StaffRow[];
  /** Read-only: the number a lead's call-back rings. The club asks Kyck to change it. */
  inboundCalls: { enabled: boolean; primaryNumber: string | null };
  /** The club's two spoken messages (null = the default) and whether Kyck has locked them (read-only). */
  messages: { whisperMessage: string | null; missedCallMessage: string | null; locked: boolean };
  /** Speed to Lead is set up for this club (the nav link shows only then). */
  usable: boolean;
}

/** The club's OWN call log (no delete: only Kyck deletes a recording). */
export const fetchPortalCalls = (accountId: string) => api<{ calls: CallLogRow[] }>(`${BASE}/clubs/${encodeURIComponent(accountId)}/calls`);

export const fetchPortalRecordingUrl = (kind: CallLogRow['kind'], id: string) =>
  api<{ url: string; expiresAt: string }>(`${BASE}/calls/${kind}/${encodeURIComponent(id)}/recording-url`);

export const confirmPortalCallOutcome = (kind: CallLogRow['kind'], id: string, outcome: string) =>
  api<{ outcome: unknown }>(`${BASE}/calls/${kind}/${encodeURIComponent(id)}/outcome`, {
    method: 'POST',
    body: JSON.stringify({ outcome }),
  });

export const fetchPortalLeadHistory = (handle: { call: string } | { conversation: string }, cursor: string | null) => {
  const q = new URLSearchParams('call' in handle ? { call: handle.call } : { conversation: handle.conversation });
  if (cursor) q.set('cursor', cursor);
  return api<LeadHistoryPage>(`${BASE}/lead-history?${q.toString()}`);
};

export const fetchPortalStaffClubs = () => api<{ clubs: PortalSpeedToLeadClub[] }>(`${BASE}/clubs`);

export const savePortalStaffList = (accountId: string, staffList: SpeedToLeadStaffEntry[]) =>
  api<{ rows: StaffRow[] }>(`${BASE}/clubs/${encodeURIComponent(accountId)}/staff-list`, {
    method: 'PUT',
    body: JSON.stringify({ staffList }),
  });

export const savePortalMessages = (accountId: string, body: { whisperMessage: string | null; missedCallMessage: string | null }) =>
  api<{ messages: PortalSpeedToLeadClub['messages'] }>(`${BASE}/clubs/${encodeURIComponent(accountId)}/messages`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// ---- Stage 4.5C: the club's OWN lead list, funnel and lead-level actions. The server scopes everything to the viewer's clubs.
export const fetchPortalLeads = (accountId: string, filters: LeadFilters, cursor: string | null) =>
  api<LeadListPage>(`${BASE}/leads${leadQueryString(filters, { accountId, cursor })}`);
export const fetchPortalLeadFunnel = (accountId: string, range: { receivedFrom?: string; receivedTo?: string }) =>
  api<LeadFunnel>(`${BASE}/leads/funnel${funnelQueryString(range, accountId)}`);
export const setPortalLeadOutcome = (leadId: string, outcome: LeadOutcome) =>
  api<{ outcome: string }>(`${BASE}/leads/${encodeURIComponent(leadId)}/outcome`, { method: 'POST', body: JSON.stringify({ outcome }) });
export const setPortalLeadNeedsPerson = (leadId: string, on: boolean) =>
  api<{ needsPerson: boolean }>(`${BASE}/leads/${encodeURIComponent(leadId)}/needs-person`, { method: 'POST', body: JSON.stringify({ on }) });

// ---- Leads centre v2, Part B: call a lead through our system (needs the `kyckconnect_contact` permission; the server answers 403 without it).
export const fetchPortalLeadCallOptions = (leadId: string) => api<LeadCallOptions>(`${BASE}/leads/${encodeURIComponent(leadId)}/call-options`);
export const startPortalLeadCall = (leadId: string, teamMemberId: string) =>
  api<{ started: true }>(`${BASE}/leads/${encodeURIComponent(leadId)}/call`, { method: 'POST', body: JSON.stringify({ teamMemberId }) });
// ---- Part C: text a lead, and the Sam switch (the same `kyckconnect_contact` permission; the server answers 403 without it).
export const sendPortalLeadText = (leadId: string, text: string) =>
  api<{ ok: true; messageId: string }>(`${BASE}/leads/${encodeURIComponent(leadId)}/text`, { method: 'POST', body: JSON.stringify({ text }) });
export const setPortalLeadSamPaused = (leadId: string, paused: boolean) =>
  api<{ paused: boolean }>(`${BASE}/leads/${encodeURIComponent(leadId)}/sam`, { method: 'POST', body: JSON.stringify({ paused }) });

// ---- Phase 2, Part B: the shared Leads split view for the club. The server scopes everything to the viewer's clubs; the full
// phone number comes back in the DETAIL only, and no request here ever carries one.
export const fetchPortalLeadSplit = (accountId: string | null, filters: LeadSplitFilters, cursor: string | null) =>
  api<LeadSplitPage>(`${BASE}/lead-split${splitQueryString(filters, { accountId, cursor })}`);
export const fetchPortalLeadSplitDetail = (leadId: string, cursor?: string | null) =>
  api<LeadSplitDetail>(`${BASE}/lead-split/${encodeURIComponent(leadId)}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);

/** The old "Lead conversations" link carried a conversation id; this resolves it to the lead's opaque id. 404 outside the viewer's clubs. */
export const fetchPortalLeadIdForConversation = (conversationId: string) =>
  api<{ leadId: string }>(`${BASE}/lead-split/by-conversation/${encodeURIComponent(conversationId)}`);
